// IndexedDB layout. One database, small number of stores, all access through this module.
import type { NotificationPrefs, Op, QuietHours, RoomState } from "@jatkuu/core";
import { type DBSchema, type IDBPDatabase, openDB } from "idb";

export type StoredIdentity = {
  id: "me";
  deviceId: string;
  publicKeyB64: string;
  memberId: string;
  /** seed encrypted with the wrapping key */
  sealedSeed: ArrayBuffer;
  iv: ArrayBuffer;
  wrappingKey: CryptoKey; // non-extractable AES-GCM key, structured-cloned into IDB
  recoveryConfirmed: boolean;
  createdAt: number;
};

export type StoredRoom = {
  roomId: string;
  roomSecret: string;
  snapshot: RoomState;
  savedAt: number;
  /** server knows about this room (create/join succeeded) */
  registered: boolean;
};

export type OutboxItem = { id: string; roomId: string; op: Op; queuedAt: number };

export type LocalKV = {
  activeRoomId?: string;
  seenCelebrations?: string[];
  catchUpSnoozedDay?: string;
  /** catch-up stops asking about days up to and including this one ("let the rest go", "start fresh") */
  catchUpClearedThrough?: string;
  /** when the card was dismissed; it comes back after two weeks */
  installCardDismissed?: boolean | number;
  aiIntroSeen?: boolean;
  /** the assistant opt-in, yours on this device; mirrored onto your member record in every room you open */
  aiEnabled?: boolean;
  notificationsAskedAt?: number;
  lastOpenDay?: string;
  notificationPrefs?: NotificationPrefs;
  quietHours?: QuietHours | undefined;
};

interface JatkuuDB extends DBSchema {
  identity: { key: "me"; value: StoredIdentity };
  rooms: { key: string; value: StoredRoom };
  outbox: { key: string; value: OutboxItem; indexes: { byRoom: string } };
  local: { key: string; value: { key: string; value: unknown } };
}

let dbPromise: Promise<IDBPDatabase<JatkuuDB>> | undefined;

export function db(): Promise<IDBPDatabase<JatkuuDB>> {
  if (!dbPromise) {
    dbPromise = openDB<JatkuuDB>("jatkuu", 1, {
      upgrade(d) {
        d.createObjectStore("identity", { keyPath: "id" });
        d.createObjectStore("rooms", { keyPath: "roomId" });
        const outbox = d.createObjectStore("outbox", { keyPath: "id" });
        outbox.createIndex("byRoom", "roomId");
        d.createObjectStore("local", { keyPath: "key" });
      },
    });
  }
  return dbPromise;
}

export async function getLocal<K extends keyof LocalKV>(key: K): Promise<LocalKV[K] | undefined> {
  const row = await (await db()).get("local", key);
  return row?.value as LocalKV[K] | undefined;
}

export async function setLocal<K extends keyof LocalKV>(key: K, value: LocalKV[K]): Promise<void> {
  await (await db()).put("local", { key, value });
}

export async function loadIdentity(): Promise<StoredIdentity | undefined> {
  return (await db()).get("identity", "me");
}

export async function saveIdentity(identity: StoredIdentity): Promise<void> {
  await (await db()).put("identity", identity);
}

export async function loadRoom(roomId: string): Promise<StoredRoom | undefined> {
  return (await db()).get("rooms", roomId);
}

export async function listRooms(): Promise<StoredRoom[]> {
  return (await db()).getAll("rooms");
}

export async function saveRoom(room: StoredRoom): Promise<void> {
  await (await db()).put("rooms", room);
}

export async function deleteRoom(roomId: string): Promise<void> {
  const d = await db();
  const tx = d.transaction(["rooms", "outbox"], "readwrite");
  await tx.objectStore("rooms").delete(roomId);
  const idx = tx.objectStore("outbox").index("byRoom");
  for (const key of await idx.getAllKeys(roomId)) await tx.objectStore("outbox").delete(key);
  await tx.done;
}

export async function enqueueOps(items: OutboxItem[]): Promise<void> {
  if (items.length === 0) return;
  const d = await db();
  const tx = d.transaction("outbox", "readwrite");
  for (const it of items) void tx.store.put(it);
  await tx.done;
}

export async function loadOutbox(roomId: string): Promise<OutboxItem[]> {
  const items = await (await db()).getAllFromIndex("outbox", "byRoom", roomId);
  return items.sort((a, b) => (a.op.hlc < b.op.hlc ? -1 : a.op.hlc > b.op.hlc ? 1 : 0));
}

export async function removeFromOutbox(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const d = await db();
  const tx = d.transaction("outbox", "readwrite");
  for (const id of ids) void tx.store.delete(id);
  await tx.done;
}

export async function wipeEverything(): Promise<void> {
  const d = await db();
  const tx = d.transaction(["identity", "rooms", "outbox", "local"], "readwrite");
  await Promise.all([
    tx.objectStore("identity").clear(),
    tx.objectStore("rooms").clear(),
    tx.objectStore("outbox").clear(),
    tx.objectStore("local").clear(),
  ]);
  await tx.done;
}
